import React, { useEffect, useState, useMemo } from 'react';
import Loader from '../../../components/UI/Loader';
import Message from '../../../components/UI/Message';
import BeginApplication from './BeginApplication';
import HousingInformation from './HousingInformation';
import IncomeInformation from './IncomeInformation';
import PreTerms from './PreTerms';
import ApplicationProcessing from './ApplicationProcessing';
import Approved from './ApplicationResult/Approved';
import Denied from './ApplicationResult/Denied';
import Delayed from './ApplicationResult/Delayed';
import OfferAccepted from './OfferAccepted';
import FundWalletInCredit from './FundWalletInCredit';
import ContinueWithoutCredit from './ContinueWithoutCredit';
import { useCreditClient } from '../../../services/credit';
import { getCreditApplicationScreen } from './helpers';
import css from './CreditApplication.module.scss';

export enum CreditApplicationScreen {
  Loader,
  BeginApplication,
  HousingInformation,
  IncomeInformation,
  PreTerms,
  ApplicationProcessing,
  Approved,
  Denied,
  Delayed,
  OfferAccepted,
  ContinueWithoutCredit,
  FundWallet
}

export default function CreditApplication({
  app,
  toggles,
  answer,
  createError,
  clearError,
  error,
  message,
  analytics
}) {
  // All of the needed info about the credit application is in this class
  const creditApplicationClient = useMemo(() => useCreditClient(), []);
  const [currentScreen, setCurrentScreen] = useState<CreditApplicationScreen>(
    CreditApplicationScreen.Loader
  );

  const authToken = app.stargatewayKey;

  const updateCreditApplicationScreen = () => {
    // Determines the new screen from the creditApplication object
    // Use this when wanting to continue to another screen
    const newScreen = getCreditApplicationScreen();
    setCurrentScreen(newScreen);
  };

  const handleCreditError = (error = '') => {
    createError({
      message:
        error ||
        'An unexpected error occurred. Please try again. If the problem continues please call support at 888-483-2645.',
      registrationStep: 'credit_application'
    });
    analytics.trackEvent('Registration Error Triggered', {
      eventProps: {
        registration_step: 'credit_application',
        error_message: error
          ? error
          : 'An unexpected error occurred. Please try again. If the problem continues please call support at 888-483-2645.'
      }
    });
  };

  useEffect(() => {
    // When component mounts use getActiveCreditApplication to get the starting screen
    const getCreditApplication = async () => {
      try {
        await creditApplicationClient.getActiveCreditApplication({
          authToken
        });
        clearError();
      } catch (error) {
        handleCreditError();
      }
      updateCreditApplicationScreen();
    };

    getCreditApplication();
  }, []);

  return (
    <div className={css.creditApplication}>
      <Message error={error} text={message} />
      {currentScreen === CreditApplicationScreen.Loader && <Loader />}
      {currentScreen === CreditApplicationScreen.BeginApplication && (
        <BeginApplication
          updateCreditApplication={updateCreditApplicationScreen}
          planName={app.answers.setPlan.pricingPlanName}
          authToken={authToken}
          moveToFundWallet={() =>
            setCurrentScreen(CreditApplicationScreen.FundWallet)
          }
        />
      )}
      {currentScreen === CreditApplicationScreen.HousingInformation && (
        <HousingInformation
          updateCreditApplication={updateCreditApplicationScreen}
          authToken={authToken}
          handleCreditError={handleCreditError}
          clearError={clearError}
        />
      )}
      {currentScreen === CreditApplicationScreen.IncomeInformation && (
        <IncomeInformation
          updateCreditApplication={updateCreditApplicationScreen}
          authToken={authToken}
          handleCreditError={handleCreditError}
          clearError={clearError}
        />
      )}
      {currentScreen === CreditApplicationScreen.PreTerms && (
        <PreTerms
          moveToApplicationProcessing={() =>
            setCurrentScreen(CreditApplicationScreen.ApplicationProcessing)
          }
          handleCreditError={handleCreditError}
          clearError={clearError}
        />
      )}
      {currentScreen === CreditApplicationScreen.ApplicationProcessing && (
        <ApplicationProcessing setCurrentScreen={setCurrentScreen} />
      )}
      {currentScreen === CreditApplicationScreen.Approved && (
        <Approved
          authToken={app.stargatewayKey}
          moveToOfferAccepted={() =>
            setCurrentScreen(CreditApplicationScreen.OfferAccepted)
          }
          moveToContinueWithoutCredit={() =>
            setCurrentScreen(CreditApplicationScreen.ContinueWithoutCredit)
          }
        />
      )}
      {currentScreen === CreditApplicationScreen.Denied && (
        <Denied
          planName={app.answers.setPlan.pricingPlanName}
          moveToFundWallet={() =>
            setCurrentScreen(CreditApplicationScreen.FundWallet)
          }
        />
      )}
      {currentScreen === CreditApplicationScreen.Delayed && (
        <Delayed
          planName={app.answers.setPlan.pricingPlanName}
          registrationToken={app.registrationToken}
          answer={answer}
        />
      )}
      {currentScreen === CreditApplicationScreen.OfferAccepted && (
        <OfferAccepted
          planName={app.answers.setPlan.pricingPlanName}
          registrationToken={app.registrationToken}
          answer={answer}
        />
      )}
      {currentScreen === CreditApplicationScreen.ContinueWithoutCredit && (
        <ContinueWithoutCredit
          planName={app.answers.setPlan.pricingPlanName}
          moveToFundWallet={() =>
            setCurrentScreen(CreditApplicationScreen.FundWallet)
          }
        />
      )}
      {currentScreen === CreditApplicationScreen.FundWallet && (
        <FundWalletInCredit
          app={app}
          toggles={toggles}
          answer={answer}
          analytics={analytics}
        />
      )}
    </div>
  );
}
